import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
	createIntervention,
	editIntervention,
	type InterventionCreateInput,
	type InterventionEditInput,
} from '@/api/adapters/patients';
import { toast } from '@/app/store/toast.store';
import { ApiError } from '@/api/error';

/**
 * Mutation hook: create a new intervention note for a patient.
 * Invalidates the intervention timeline cache on success so the
 * panel and the patient detail timeline stay in sync.
 */
export function useCreateIntervention() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: ({
			patientUid,
			input,
		}: {
			patientUid: string;
			input: InterventionCreateInput;
		}) => createIntervention(patientUid, input),
		onSuccess: (_data, { input }) => {
			// SHCT-952: the toast names the saved record's type (PRD 04 §3.4 — "{유형} 기록이 저장되었습니다.").
			const typeKey = input.type === 'call' ? 'call' : 'message';
			toast.success(t('patients.intervention.form.saveSuccess', {
				type: t(`patients.intervention.form.type.${typeKey}`),
			}));
			queryClient.invalidateQueries({ queryKey: ['intervention'] });
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('common:error.generic'));
			}
		},
	});
}

/**
 * Mutation hook: edit an existing intervention note (title + body only).
 * The note's type/target/channel/일시 are immutable — this sends only
 * the editable surface defined by {@link InterventionEditInput}.
 */
export function useEditIntervention() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: ({
			patientUid,
			noteId,
			data,
		}: {
			patientUid: string;
			noteId: string;
			data: InterventionEditInput;
		}) => editIntervention(patientUid, noteId, data),
		onSuccess: () => {
			toast.success(t('patients.intervention.form.editSuccess'));
			queryClient.invalidateQueries({ queryKey: ['intervention'] });
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('patients.intervention.form.editError'));
			}
		},
	});
}
